import React, {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { BPortal } from '../BPortal';
import s from './BModal.module.scss';
import { BButton } from '../BButton';

export interface BModalProps {
  className?: string;
  isOpen?: boolean;
  icon?: ReactNode;
  isCentered?: boolean;
  onClose?: () => void;
  lazyLoad?: boolean;
  children: ReactNode;
  maxWidth?: number | string;
  title: string | ReactNode;
}

const ANIMATION_DELAY = 300;

export const BModal = (props: BModalProps) => {
  const {
    children,
    className,
    onClose,
    isOpen,
    maxWidth = 840,
    lazyLoad,
    title,
    icon,
    isCentered,
  } = props;
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const timerRef = useRef() as MutableRefObject<
    ReturnType<typeof setTimeout>
  >;

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  const closeHandler = useCallback(() => {
    if (onClose) {
      setIsClosing(true);
      timerRef.current = setTimeout(() => {
        onClose();
        setIsClosing(false);
      }, ANIMATION_DELAY);
    }
  }, [onClose]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeHandler();
      }
    },
    [closeHandler],
  );

  const handlerOverlay = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', onKeyDown);
    }

    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onKeyDown]);

  const mods = {
    [s.isOpen]: isOpen,
    [s.isClosing]: isClosing,
  };

  const style: CSSProperties = {
    maxWidth,
  };

  // Если lazyLoad === true и isMounted === false
  if (lazyLoad && !isMounted) {
    return null;
  }

  return (
    <BPortal>
      <div className={cx(s.modal, mods, className)}>
        <div className={s.overlay} onClick={closeHandler}>

          <div className={s.content} onClick={handlerOverlay} style={style}>
            <header className={cx({ [s.isCentered]: isCentered })}>
              {icon && <div className={s.icon}>{icon}</div>}
              <h2 className={cx(s.title, { [s.isCenteredTitle]: isCentered })}>{title}</h2>
            </header>
            <BButton
              variant="text"
              color="secondary"
              className={s.closeButton}
              onClick={closeHandler}
            >
              <span className={s.closeIcon} />
            </BButton>

            <div className={cx(s.body, { [s.isCentered]: isCentered })}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </BPortal>
  );
};
